import { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../../constants";

/**
 * Custom hook that manages authentication logic and state
 * This hook centralizes all authentication-related functionality
 * including login, logout, token storage, and validation
 *
 * @param {Object} options - Configuration options
 * @param {string} options.redirectPath - Path to redirect unauthenticated users
 * @returns {Object} Auth methods and state
 */
const useAuth = (options = {}) => {
  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [authMode, setAuthMode] = useState("cognito");
  const [cognitoToken, setCognitoToken] = useState("");

  // Configuration check
  const isCognitoConfigured =
    typeof window !== "undefined" &&
    window.COGNITO_CONFIG &&
    window.COGNITO_CONFIG.userPoolId &&
    window.COGNITO_CONFIG.clientId;

  // Check for stored session on initial load
  useEffect(() => {
    const storedToken = sessionStorage.getItem("cognitoIdToken");
    if (storedToken) {
      setCognitoToken(storedToken);

      // Parse token to get user info
      try {
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        setUserInfo({
          email: payload.email,
          sub: payload.sub,
          groups: payload["cognito:groups"] || [],
        });

        // Check if user is admin
        if (payload["cognito:groups"]?.includes("Admins")) {
          setUserRole("admin");
          setIsLoggedIn(true);
        } else if (options.redirectPath) {
          // Redirect to regular user login page if not an admin
          window.location.href = options.redirectPath;
          return;
        }

        // Check expiration
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < currentTime) {
          handleLogout();
          setErrorMessage("Your session has expired. Please login again.");
        }
      } catch (e) {
        console.error("Error parsing JWT token:", e);
      }
    }
  }, [options.redirectPath]);

  /**
   * Handle Cognito login
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<boolean>} Success status
   */
  const handleCognitoLogin = useCallback(
    async (email, password) => {
      if (!email || !password) {
        setErrorMessage("Email and password are required");
        return false;
      }

      if (!isCognitoConfigured) {
        setErrorMessage("Cognito is not properly configured");
        return false;
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        // Import Cognito dynamically to avoid issues with SSR
        const { CognitoUserPool, CognitoUser, AuthenticationDetails } =
          await import("amazon-cognito-identity-js");

        const userPool = new CognitoUserPool({
          UserPoolId: window.COGNITO_CONFIG.userPoolId,
          ClientId: window.COGNITO_CONFIG.clientId,
        });

        const authenticationDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        return new Promise((resolve, reject) => {
          cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (result) => {
              // Get the ID token
              const idToken = result.getIdToken().getJwtToken();

              // Store token
              sessionStorage.setItem("cognitoIdToken", idToken);
              setCognitoToken(idToken);

              // Parse user info from token
              try {
                const payload = JSON.parse(atob(idToken.split(".")[1]));
                const userInfoData = {
                  email: payload.email,
                  sub: payload.sub,
                  groups: payload["cognito:groups"] || [],
                };

                setUserInfo(userInfoData);

                // Check if user is an admin
                if (payload["cognito:groups"]?.includes("Admins")) {
                  setUserRole("admin");
                  setIsLoggedIn(true);
                  resolve(true);
                } else {
                  // Redirect non-admin users to the regular login page
                  if (options.redirectPath) {
                    window.location.href = options.redirectPath;
                  }
                  resolve(false);
                }
              } catch (e) {
                console.error("Error parsing JWT token:", e);
                reject(new Error("Error parsing authentication response"));
              }

              setIsLoading(false);
            },
            onFailure: (err) => {
              console.error("Authentication error:", err);
              setErrorMessage(err.message || "Authentication failed");
              setIsLoading(false);
              reject(err);
            },
            newPasswordRequired: (userAttributes, requiredAttributes) => {
              // Handle new password required (first login)
              setErrorMessage(
                "You need to change your password. Please contact an administrator."
              );
              setIsLoading(false);
              reject(new Error("New password required"));
            },
          });
        });
      } catch (error) {
        setErrorMessage("Login failed: " + error.message);
        setIsLoading(false);
        return false;
      }
    },
    [isCognitoConfigured, options.redirectPath]
  );

  /**
   * Handle legacy password login
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<boolean>} Success status
   */
  const handlePasswordLogin = useCallback(async (email, password) => {
    // Validation
    if (!email || !password) {
      setErrorMessage("Email and password are required");
      return false;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      // Test authentication with password
      const response = await fetch(API_BASE_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          password: password,
        },
      });

      if (!response.ok) {
        throw new Error("Authentication failed");
      }

      // Store password for API calls
      sessionStorage.setItem("apiPassword", password);
      sessionStorage.setItem("userEmail", email);
      setIsLoggedIn(true);
      setUserInfo({ email }); // Simple user info for password auth
      setIsLoading(false);
      return true;
    } catch (error) {
      setErrorMessage("Login failed: " + error.message);
      setIsLoading(false);
      return false;
    }
  }, []);

  /**
   * Generic login handler that uses either Cognito or password auth
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} authType - Authentication type ('cognito' or 'password')
   * @returns {Promise<boolean>} Success status
   */
  const login = useCallback(
    async (email, password, authType = authMode) => {
      if (authType === "cognito" && isCognitoConfigured) {
        return handleCognitoLogin(email, password);
      } else {
        return handlePasswordLogin(email, password);
      }
    },
    [authMode, handleCognitoLogin, handlePasswordLogin, isCognitoConfigured]
  );

  /**
   * Logout function
   */
  const handleLogout = useCallback(() => {
    if (authMode === "cognito" && isCognitoConfigured) {
      // Clear token
      sessionStorage.removeItem("cognitoIdToken");
      setCognitoToken("");

      // With direct integration, we don't need to redirect for logout
      try {
        const { CognitoUserPool } = require("amazon-cognito-identity-js");
        const userPool = new CognitoUserPool({
          UserPoolId: window.COGNITO_CONFIG.userPoolId,
          ClientId: window.COGNITO_CONFIG.clientId,
        });

        const currentUser = userPool.getCurrentUser();
        if (currentUser) {
          currentUser.signOut();
        }
      } catch (error) {
        console.error("Error during Cognito logout:", error);
      }
    } else {
      // Simple password logout
      sessionStorage.removeItem("apiPassword");
      sessionStorage.removeItem("userEmail");
    }

    setIsLoggedIn(false);
    setUserInfo(null);
    setUserRole(null);
    setCognitoToken("");
  }, [authMode, isCognitoConfigured]);

  /**
   * Parses and validates token, returning user info if valid
   * @param {string} token - The JWT token
   * @returns {Object|null} User info or null if invalid
   */
  const parseAndValidateToken = useCallback((token) => {
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));

      // Check expiration
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < currentTime) {
        return null;
      }

      return {
        email: payload.email,
        sub: payload.sub,
        groups: payload["cognito:groups"] || [],
        isAdmin: payload["cognito:groups"]?.includes("Admins") || false,
      };
    } catch (e) {
      console.error("Error parsing JWT token:", e);
      return null;
    }
  }, []);

  /**
   * Switch between authentication modes
   * @param {string} mode - The auth mode to switch to ('cognito' or 'password')
   */
  const switchAuthMode = useCallback((mode) => {
    if (mode === "cognito" || mode === "password") {
      setAuthMode(mode);
    }
  }, []);

  /**
   * Gets authorization headers for API requests
   * @returns {Object} Headers object
   */
  const getAuthHeaders = useCallback(() => {
    const headers = { "Content-Type": "application/json" };

    if (authMode === "cognito" && cognitoToken) {
      headers["Authorization"] = `Bearer ${cognitoToken}`;
    } else {
      const storedPassword = sessionStorage.getItem("apiPassword");
      if (storedPassword) {
        headers["password"] = storedPassword;
      }
    }

    return headers;
  }, [authMode, cognitoToken]);

  return {
    // State
    isLoggedIn,
    isLoading,
    errorMessage,
    userInfo,
    userRole,
    authMode,
    cognitoToken,

    // Methods
    login,
    logout: handleLogout,
    switchAuthMode,
    getAuthHeaders,
    parseAndValidateToken,
    clearError: () => setErrorMessage(""),
  };
};

export default useAuth;
